import type { Hex } from "@/color/oklch";
import { type ColorId, hexOf, type Resolved, resolveTheme } from "@/model/resolve";
import { slugify, type Theme } from "@/model/theme";

export interface ExportContext {
  theme: Theme;
  resolved: Resolved;
  /** `gruvbox-dark` */
  slug: string;
  /** `themes-gruvbox-dark`: the name the theme gets inside each app. */
  appName: string;
  color: (id: ColorId) => Hex;
}

export function makeContext(theme: Theme, resolved = resolveTheme(theme)): ExportContext {
  const slug = slugify(theme.name);
  return {
    theme,
    resolved,
    slug,
    appName: `themes-${slug}`,
    color: (id) => hexOf(resolved, id) ?? "#ff00ff",
  };
}

export interface ExportFile {
  /** Where the file goes, with `~` for the home directory. */
  path: string;
  /** File name used for downloads. */
  filename: string;
  /** Code-fence language. */
  language: string;
  content: string;
}

export interface ExportResult {
  files: ExportFile[];
  /** Ordered instructions for a person or an agent. Markdown inline syntax allowed. */
  steps: string[];
  /** Commands that prove the change worked. */
  verify: string[];
  /** Caveats worth knowing (approximations, limitations). */
  notes: string[];
}

export type ExporterId =
  | "ghostty"
  | "herdr"
  | "neovim"
  | "weechat"
  | "cursor-agent"
  | "claude-code"
  | "base16"
  | "base24"
  | "css"
  | "theme-json";

export interface Exporter {
  id: ExporterId;
  label: string;
  /** One of the user's own apps (shown first, part of the "All apps" bundle). */
  app: boolean;
  description: string;
  run: (ctx: ExportContext) => ExportResult;
}

export const GENERATED_BY = "generated by https://themes.utimura.io";
